import type { HTMLAttributes, ReactNode, Ref } from "react";

import { alert, alertBody, alertIcon } from "~/components/Alert.css";

export type AlertVariant = "info" | "success" | "warning" | "danger";

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  /** Semantic color. Defaults to "info". */
  variant?: AlertVariant;
  /**
   * Optional icon slot rendered to the left of the body. Pass an
   * emoji, SVG, or any ReactNode. Omit for a bodyless layout.
   */
  icon?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Inline banner for status messages. Renders a <div role="status">
 * (or role="alert" for danger) with a tinted surface and optional
 * left-side icon. Not dismissible — compose a dismiss button inside
 * children if needed.
 */
export function Alert({
  variant = "info",
  icon,
  className,
  children,
  ref,
  ...rest
}: AlertProps) {
  const variantClass = alert[variant];
  const merged =
    className !== undefined && className.length > 0
      ? `${variantClass} ${className}`
      : variantClass;
  const role = variant === "danger" ? "alert" : "status";
  return (
    <div ref={ref} role={role} className={merged} {...rest}>
      {icon !== undefined && icon !== null && (
        <span className={alertIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      <div className={alertBody}>{children}</div>
    </div>
  );
}
